import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ArrowRight, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.status === 'success') {
        setSubmitted(true);
        toast.success('Message sent successfully!');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-white paper-texture" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-sm font-medium tracking-widest uppercase text-[#3EACDB]">Contact Us</span>
          <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter uppercase mt-4">
            Get in <span className="text-[#3EACDB]">Touch</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl">
            Visit our Midtown Manhattan location, give us a call, or send us a message. We're here to help with all your printing needs.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-16 md:py-24 bg-zinc-50" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <a 
                  href="tel:+12129711334" 
                  className="flex items-start gap-4 p-6 bg-white border border-zinc-200 hover:border-[#3EACDB]/50 transition-colors"
                  data-testid="contact-phone-card"
                >
                  <div className="w-12 h-12 bg-[#3EACDB] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-zinc-900">Phone</p>
                    <p className="text-[#3EACDB] text-lg font-medium">(212) 971-1334</p>
                    <p className="text-sm text-zinc-500 mt-1">Click to call</p>
                  </div>
                </a>

                <a 
                  href="mailto:Jobs@charlesprint.com" 
                  className="flex items-start gap-4 p-6 bg-white border border-zinc-200 hover:border-[#3EACDB]/50 transition-colors"
                  data-testid="contact-email-card"
                >
                  <div className="w-12 h-12 bg-[#3EACDB] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-zinc-900">Email</p>
                    <p className="text-[#3EACDB] text-lg font-medium">Jobs@charlesprint.com</p>
                    <p className="text-sm text-zinc-500 mt-1">We respond within 24 hours</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-6 bg-white border border-zinc-200">
                  <div className="w-12 h-12 bg-[#3EACDB] rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-zinc-900">Address</p>
                    <p className="text-zinc-700">247 W 35th St, Suite #203</p>
                    <p className="text-zinc-700">New York, NY 10001</p>
                    <p className="text-sm text-zinc-500 mt-1">Garment District, Midtown Manhattan</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white border border-zinc-200">
                  <div className="w-12 h-12 bg-[#3EACDB] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-zinc-900">Business Hours</p>
                    <div className="mt-2 space-y-1 text-zinc-700">
                      <p>Monday - Friday: 9:00 AM - 5:30 PM</p>
                      <p>Saturday: By Appointment</p>
                      <p>Sunday: By Appointment</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link to="/quote" data-testid="contact-quote-link">
                  <Button className="h-12 px-6 text-sm font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm">
                    Request a Quote Instead
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white border border-zinc-200 p-8 md:p-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">Send a Message</h2>
              <p className="text-zinc-600 mb-8">Have a question? We'd love to hear from you.</p>

              {submitted ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2">Message Sent!</h3>
                  <p className="text-zinc-600">Thank you for contacting us. We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-2 h-12 rounded-sm border-zinc-300 focus:ring-[#3EACDB] focus:border-[#3EACDB]"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 h-12 rounded-sm border-zinc-300 focus:ring-[#3EACDB] focus:border-[#3EACDB]"
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium">Phone (Optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 h-12 rounded-sm border-zinc-300 focus:ring-[#3EACDB] focus:border-[#3EACDB]"
                      placeholder="(555) 123-4567"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-medium">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="mt-2 rounded-sm border-zinc-300 focus:ring-[#3EACDB] focus:border-[#3EACDB] resize-none"
                      placeholder="How can we help you?"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 text-sm font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 md:py-24 bg-white" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold">Find Us</h2>
            <p className="mt-2 text-zinc-600">Located in the heart of Midtown Manhattan</p>
          </div>
          <div className="map-container rounded-sm overflow-hidden border border-zinc-200">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.1838433442!2d-73.99229962346907!3d40.75359013539413!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259aea4e3ea25%3A0x8c5ec29fa7d6d2a5!2s247%20W%2035th%20St%2C%20New%20York%2C%20NY%2010001!5e0!3m2!1sen!2sus!4v1699000000000!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Charles Print Location"
              data-testid="google-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
